import { forwardRef, Ref, useRef, useState, FocusEvent } from 'react';
import { Field, Input, InputValue, TextInputProps } from '@optimus-web/core';
import { useMergeRefs } from '@mews/framework';

const empty = '';

export interface ControlledNumberInputProps extends Omit<TextInputProps, 'value' | 'onChange'> {
    value?: number | null;

    /**
     * OnChange triggers only when a valid number input is entered, or on blur with
     * a final value after the input is concluded.
     *
     * If the entered string is not parseable into a valid number, the input reverts
     * to a last valid value after blur.
     *
     * @param value - A valid number parsed from the input
     */
    onChange?: (value: number | undefined | null) => void;
}

const BaseNumberInput = forwardRef(
    (
        { value, onChange, onBlur, size, placeholder, type, ...other }: ControlledNumberInputProps,
        ref: Ref<HTMLInputElement>
    ) => {
        const localRef = useRef<HTMLInputElement>(null);
        const mergedRefs = useMergeRefs(ref, localRef);
        const [lastValidValue, setLastValidValue] = useState<number | null | undefined>(value);
        const [showPlaceholder, setShowPlaceholder] = useState(!value);

        /**
         * Since the NumberInput is meant to be uncontrolled component, this allows is to reset to a change of value
         * from external source.
         */
        if (value !== lastValidValue && value != null && localRef.current) {
            localRef.current.value = value.toString();
        }

        const handleChange = (e: unknown, inputValue: InputValue) => {
            setShowPlaceholder(!inputValue);

            const parsedValue = parse(inputValue as string);
            if (!Number.isNaN(parsedValue)) {
                setLastValidValue(parsedValue);
                onChange?.(parsedValue);
            }
        };

        const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
            const parsedValue = parse(e.currentTarget.value);
            const nextValue = Number.isNaN(parsedValue) ? lastValidValue : parsedValue;

            onBlur?.(e);
            setLastValidValue(nextValue);
            setShowPlaceholder(!nextValue);
            onChange?.(nextValue);
            e.currentTarget.value = String(nextValue ?? empty);
        };

        return (
            <Input
                defaultValue={lastValidValue as number | undefined}
                onBlur={handleBlur}
                onChange={handleChange}
                size={size}
                placeholder={showPlaceholder ? placeholder : undefined}
                ref={mergedRefs}
                type="number"
                {...other}
            />
        );
    }
);

const parse = (rawValue: string): number | null => {
    if (rawValue === empty) return null;
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? null : parsed;
};

export const ControlledNumberInput = Field(BaseNumberInput);
